import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Spinner } from 'react-bootstrap';

/**
 * WFBackofficeGuard - Higher Order Component to restrict access to Wishfin Backoffice dashboard
 * Redirects to login page if:
 * 1. User is not authenticated
 * 2. User role is not 'wishfin_backoffice'
 */
const WFBackofficeGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const { isAuthenticated, getCurrentUser } = useAuth();

  useEffect(() => {
    // Instead of async function, use regular function to avoid potential infinite loop
    const checkUserAccess = () => {
      try {
        console.log("Starting WFBackofficeGuard check");
        // Check if user is authenticated
        const isLoggedIn = isAuthenticated();
        console.log("isAuthenticated result:", isLoggedIn);
        
        if (!isLoggedIn) {
          console.log("User not logged in, redirecting to login");
          setIsVerified(false);
          setLoading(false);
          return;
        }
        
        // Get user details directly from localStorage to avoid dependency cycle
        const storedUser = localStorage.getItem('user');
        const user = storedUser ? JSON.parse(storedUser) : null;
        
        // Check if user has WFBACKOFFICE role
        const hasBackofficeRole = user?.user_type === 'WFBACKOFFICE';
        
        // Log status for debugging
        console.log("WF Backoffice Access Status:", {
          isLoggedIn,
          user_type: user?.user_type,
          hasBackofficeRole
        });
        
        // Set verification status
        setIsVerified(isLoggedIn && hasBackofficeRole);
      } catch (error) {
        console.error('Error checking backoffice access:', error);
        // If error occurs, redirect to login as a fallback
        setIsVerified(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkUserAccess();
  }, [isAuthenticated]);
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" />
        <span className="ms-2">Verifying access...</span>
      </div>
    );
  }
  
  // If not verified, redirect to login page
  if (!isVerified) {
    return <Navigate to="/login" replace />;
  }
  
  // If verified, render children
  return children;
};

export default WFBackofficeGuard;